import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Store, Search, ChevronLeft, ChevronRight, Copy, Loader2, Pencil, Trash2, Ban, FileText } from 'lucide-react';
import { toast } from '../../lib/safeToast';
import { getApiErrorMessage } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const getRoleLabel = (role) => {
  const labels = { admin: 'Super Admin', merchant_admin: 'Merchant Admin', manager: 'Manager', viewer: 'Viewer' };
  return labels[role] || role;
};

const getRoleBadgeClass = (role) => {
  switch (role) {
    case 'admin': return 'bg-[#1C472B] text-white';
    case 'merchant_admin': return 'bg-[#0B6445] text-white';
    case 'manager': return 'bg-[#A5E0D2] text-[#0F2818]';
    default: return 'bg-slate-100 text-slate-700';
  }
};

const getKycBadgeClass = (kyc) => {
  switch (kyc) {
    case 'approved': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    case 'co_recommended_approve': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'co_recommended_reject': return 'bg-red-50 text-red-700 border-red-200';
    case 'in_review': return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'info_requested': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

function formatAddress(addr) {
  if (!addr || typeof addr !== 'object') return '—';
  const parts = [
    addr.line1,
    addr.line2,
    [addr.city, addr.state, addr.postcode].filter(Boolean).join(' '),
    addr.country,
  ].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

const DEBOUNCE_MS = 350;

const BackOfficeMerchants = () => {
  const { api } = useAuth();
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({ current_page: 1, per_page: 25, total: 0, last_page: 0 });
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [detailUser, setDetailUser] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [modifyOpen, setModifyOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [kybDetails, setKybDetails] = useState(null);
  const [kybDetailsOpen, setKybDetailsOpen] = useState(false);
  const [kybDetailsLoading, setKybDetailsLoading] = useState(false);

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 25 });
      if (search) params.set('search', search);
      const res = await api.get(`/backoffice/merchants?${params}`);
      setData(res.data?.data || []);
      setMeta(res.data?.meta || meta);
    } catch (e) {
      toast.error('Failed to load merchants');
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMerchants();
  }, [page, search]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleSearchClear = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const copyToClipboard = async (text, label = 'Value') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const openUserDetail = async (userId) => {
    setDetailUser(null);
    setDetailLoading(true);
    try {
      const res = await api.get(`/users/${userId}`);
      setDetailUser(res.data?.data || null);
    } catch {
      toast.error('Failed to load user');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (!detailUser?.id || newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put(`/users/${detailUser.id}/password`, { new_password: newPassword });
      toast.success('Password updated');
      setNewPassword('');
      setConfirmPassword('');
      setChangePasswordOpen(false);
      openUserDetail(detailUser.id);
      fetchMerchants();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to update password'));
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!detailUser?.id) return;
    setActionLoading(true);
    try {
      await api.delete(`/users/${detailUser.id}`);
      toast.success('Merchant user deleted');
      setDeleteConfirmOpen(false);
      setDetailUser(null);
      fetchMerchants();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to delete'));
    } finally {
      setActionLoading(false);
    }
  };

  const openModify = () => {
    setEditName(detailUser?.name ?? '');
    setEditRole(detailUser?.role ?? 'merchant_admin');
    setModifyOpen(true);
  };

  const handleModify = async (e) => {
    e.preventDefault();
    if (!detailUser?.id) return;
    setActionLoading(true);
    try {
      if (editName.trim() !== (detailUser.name || '')) {
        await api.patch(`/users/${detailUser.id}`, { name: editName.trim() });
      }
      if (editRole !== detailUser.role) {
        await api.put(`/users/${detailUser.id}/role`, { role: editRole });
      }
      toast.success('Merchant updated');
      setModifyOpen(false);
      openUserDetail(detailUser.id);
      fetchMerchants();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to update'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async () => {
    if (!detailUser?.id) return;
    const next = detailUser.account_status === 'suspended' ? 'active' : 'suspended';
    setActionLoading(true);
    try {
      await api.patch(`/users/${detailUser.id}`, { account_status: next });
      toast.success(next === 'suspended' ? 'Merchant suspended' : 'Merchant activated');
      openUserDetail(detailUser.id);
      fetchMerchants();
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to update status'));
    } finally {
      setActionLoading(false);
    }
  };

  const openKybDetails = async () => {
    if (!detailUser?.id || !detailUser.onboarding_complete) return;
    setKybDetails(null);
    setKybDetailsOpen(true);
    setKybDetailsLoading(true);
    try {
      const res = await api.get(`/backoffice/users/${detailUser.id}/kyb-details`);
      setKybDetails(res.data?.onboarding != null || res.data?.company_information ? res.data : null);
    } catch (err) {
      toast.error(getApiErrorMessage(err.response?.data?.detail, 'Failed to load KYB details'));
      setKybDetailsOpen(false);
    } finally {
      setKybDetailsLoading(false);
    }
  };

  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4">
          <div>
            <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
              <Store className="w-7 h-7 text-[#0B6445]" />
              All Merchants
            </h1>
            <p className="text-[#0E1B16]/70 mt-2">Search and filter platform merchants.</p>
          </div>
          <Link to="/backoffice/merchants/add">
            <Button className="bg-[#0B6445] hover:bg-[#095b3d] text-white">Add New Merchant</Button>
          </Link>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader>
            <form onSubmit={(e) => e.preventDefault()} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#0E1B16]/50" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-9"
                  aria-label="Search merchants"
                />
              </div>
              {searchInput && (
                <Button type="button" variant="outline" onClick={handleSearchClear}>
                  Clear
                </Button>
              )}
            </form>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
            ) : data.length === 0 ? (
              <p className="text-center py-12 text-[#0E1B16]/60">No merchants found.</p>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[#DCE4E0]">
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Name</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Email</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Role</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Status</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">KYC Status</th>
                        <th className="text-left py-3 px-2 font-semibold text-[#073B2E]">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.map((row) => (
                        <tr
                          key={row.id}
                          className="border-b border-[#DCE4E0]/80 hover:bg-[#F8FAFC] cursor-pointer"
                          onClick={() => openUserDetail(row.id)}
                          title="Click to view details and copy temp password"
                        >
                          <td className="py-3 px-2">{row.name || '—'}</td>
                          <td className="py-3 px-2">{row.email}</td>
                          <td className="py-3 px-2">{row.role || '—'}</td>
                          <td className="py-3 px-2">
                            <span className={row.account_status === 'suspended' ? 'text-amber-600' : 'text-emerald-600'}>
                              {row.account_status === 'suspended' ? 'Inactive' : 'Active'}
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <span className={
                              row.kyc_status === 'approved' ? 'text-green-600' :
                              row.kyc_status === 'co_recommended_approve' ? 'text-emerald-600' :
                              row.kyc_status === 'co_recommended_reject' ? 'text-red-600' :
                              row.kyc_status === 'in_review' ? 'text-amber-600' :
                              row.kyc_status === 'rejected' ? 'text-red-600' :
                              'text-slate-500'
                            }>
                              {{ co_recommended_approve: 'Compliance Approved', co_recommended_reject: 'Compliance Rejected', in_review: 'In Review', info_requested: 'Info Requested', approved: 'Approved', rejected: 'Rejected', pending: 'Pending' }[row.kyc_status] || row.kyc_status || 'Pending'}
                            </span>
                          </td>
                          <td className="py-3 px-2">{row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {meta.last_page > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#DCE4E0]">
                    <p className="text-sm text-[#0E1B16]/70">Page {meta.current_page} of {meta.last_page} ({meta.total} total)</p>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        <Dialog open={!!detailUser} onOpenChange={(open) => { if (!open) { setDetailUser(null); setChangePasswordOpen(false); setNewPassword(''); setConfirmPassword(''); setModifyOpen(false); } }}>
          <DialogContent className="sm:max-w-xl min-w-[28rem]">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">Merchant details</DialogTitle>
              <DialogDescription className="text-slate-600">
                View credentials and password options
              </DialogDescription>
            </DialogHeader>
            {detailLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
              </div>
            ) : detailUser ? (
              <div className="mt-2">
                {/* Name + KYC tag in header */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <span className="font-semibold text-[#0F172A] text-lg">{detailUser.name || '—'}</span>
                  <Badge variant="outline" className={getKycBadgeClass(detailUser.kyc_status || 'pending')}>
                    {{ co_recommended_approve: 'Compliance Approved', co_recommended_reject: 'Compliance Rejected', in_review: 'In Review', info_requested: 'Info Requested', approved: 'Approved', rejected: 'Rejected', pending: 'Pending' }[detailUser.kyc_status] || detailUser.kyc_status || 'Pending'}
                  </Badge>
                </div>
                {/* Labels: explicit contrast so hover doesn't affect visibility */}
                <div className="space-y-4">
                  <div>
                    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Name</p>
                    <p className="font-medium text-[#0F172A] mt-0.5">{detailUser.name || '—'}</p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Email</p>
                    <p className="font-mono text-sm text-[#0F172A] mt-0.5">{detailUser.email}</p>
                  </div>
                  <div className="flex flex-wrap items-baseline gap-4 pb-1">
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Role</p>
                      <p className="mt-0.5">
                        <Badge className={getRoleBadgeClass(detailUser.role)}>{getRoleLabel(detailUser.role)}</Badge>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Status</p>
                      <p className="mt-0.5">
                        <Badge variant="outline" className={detailUser.account_status === 'suspended' ? 'border-amber-300 text-amber-700 bg-amber-50' : 'border-emerald-300 text-emerald-700 bg-emerald-50'}>
                          {detailUser.account_status === 'suspended' ? 'Inactive' : 'Active'}
                        </Badge>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions: View details (KYB), Modify, Suspend, Delete (only when temp password = not yet started KYC) */}
                <div className="flex flex-nowrap gap-2 mt-5 pt-4 border-t border-[#DCE4E0]">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    title={detailUser.onboarding_complete ? 'View full KYB/KYC details' : 'Merchant has not submitted KYB/KYC yet'}
                    className={detailUser.onboarding_complete ? 'border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E] shrink-0' : 'border-slate-200 text-slate-400 cursor-not-allowed shrink-0'}
                    onClick={openKybDetails}
                    disabled={actionLoading || !detailUser.onboarding_complete}
                  >
                    <FileText className="w-4 h-4 mr-1.5" />
                    View details
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900 shrink-0"
                    onClick={openModify}
                    disabled={actionLoading}
                  >
                    <Pencil className="w-4 h-4 mr-1.5" />
                    Modify
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className={detailUser.account_status === 'suspended' ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-900 shrink-0' : 'border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-900 shrink-0'}
                    onClick={handleSuspend}
                    disabled={actionLoading}
                  >
                    <Ban className="w-4 h-4 mr-1.5" />
                    {detailUser.account_status === 'suspended' ? 'Activate' : 'Suspend'}
                  </Button>
                  {detailUser.temp_password && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      title="Only available before merchant registers and starts KYB/KYC; use Suspend for compliance after that"
                      className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-900 shrink-0"
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={actionLoading}
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Delete
                    </Button>
                  )}
                </div>

                {/* Spacing before temp password block */}
                {detailUser.temp_password ? (
                  <div className="mt-6 rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4">
                    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Temporary password</p>
                    <p className="text-sm text-slate-600 mb-2">User has not changed password yet. Share this with them.</p>
                    <div className="flex items-center justify-between gap-2">
                      <code className="font-mono text-sm text-[#0F172A] break-all flex-1">{detailUser.temp_password}</code>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="shrink-0 border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                        onClick={() => copyToClipboard(detailUser.temp_password, 'Password')}
                      >
                        <Copy className="w-4 h-4 mr-1" />
                        Copy
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6">
                    {!changePasswordOpen ? (
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full border-[#0B6445] text-[#0B6445] hover:bg-[#E6F7F3] hover:text-[#073B2E]"
                        onClick={() => setChangePasswordOpen(true)}
                      >
                        Change password
                      </Button>
                    ) : (
                      <form onSubmit={handleSetPassword} className="space-y-3 rounded-xl border border-[#DCE4E0] p-4 bg-[#F8FAFC]">
                        <Label className="text-slate-600">New password</Label>
                        <Input
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          placeholder="Min 8 characters"
                          minLength={8}
                          required
                          className="text-[#0F172A]"
                        />
                        <Label className="text-slate-600">Confirm password</Label>
                        <Input
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Repeat new password"
                          minLength={8}
                          required
                          className="text-[#0F172A]"
                        />
                        <div className="flex gap-2">
                          <Button type="button" variant="outline" onClick={() => { setChangePasswordOpen(false); setNewPassword(''); setConfirmPassword(''); }} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">
                            Cancel
                          </Button>
                          <Button type="submit" className="bg-[#0B6445] hover:bg-[#095b3d] text-white" disabled={passwordSaving}>
                            {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* Modify form (inline when modifyOpen) */}
                {modifyOpen && (
                  <form onSubmit={handleModify} className="mt-6 rounded-xl border border-[#DCE4E0] bg-slate-50/80 p-4 space-y-3">
                    <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">Edit merchant</p>
                    <div>
                      <Label className="text-slate-600">Name</Label>
                      <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="mt-1 text-[#0F172A]" />
                    </div>
                    <div>
                      <Label className="text-slate-600">Role</Label>
                      <Select value={editRole} onValueChange={setEditRole}>
                        <SelectTrigger className="mt-1 text-[#0F172A]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="merchant_admin">Merchant Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="viewer">Viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button type="button" variant="outline" onClick={() => setModifyOpen(false)} className="border-slate-300 text-slate-700 hover:bg-slate-100 hover:text-slate-900">Cancel</Button>
                      <Button type="submit" className="bg-[#0B6445] hover:bg-[#095b3d] text-white" disabled={actionLoading}>
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="text-[#0F172A]">Delete merchant user?</AlertDialogTitle>
              <AlertDialogDescription className="text-slate-600">
                This will permanently remove {detailUser?.name || detailUser?.email} from the platform. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="text-slate-600">Cancel</AlertDialogCancel>
              <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white" disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Full KYB/KYC details dialog */}
        <Dialog open={kybDetailsOpen} onOpenChange={(open) => { if (!open) { setKybDetailsOpen(false); setKybDetails(null); } }}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-[#0F172A]">Merchant full details (KYB/KYC)</DialogTitle>
              <DialogDescription className="text-slate-600">
                Information submitted during KYB/KYC process
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto flex-1 pr-2 -mr-2 space-y-6 mt-2">
              {kybDetailsLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#0B6445]" />
                </div>
              ) : kybDetails ? (
                <>
                  {/* Company / business */}
                  <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Company</p>
                    <p className="text-sm text-[#0F172A]"><strong>Name:</strong> {kybDetails.company_information?.entity_name || kybDetails.onboarding?.business?.EntityName || kybDetails.onboarding?.business?.entityName || '—'}</p>
                    <p className="text-sm text-[#0F172A]"><strong>ABN:</strong> {kybDetails.company_information?.abn || kybDetails.onboarding?.business?.Abn || kybDetails.onboarding?.business?.abn || '—'}</p>
                    <p className="text-sm text-[#0F172A]"><strong>ACN:</strong> {kybDetails.company_information?.acn || kybDetails.onboarding?.business?.Acn || kybDetails.onboarding?.business?.acn || '—'}</p>
                    <p className="text-sm text-[#0F172A]"><strong>Entity type:</strong> {kybDetails.company_information?.entity_type || kybDetails.onboarding?.entity_type || kybDetails.onboarding?.business?.EntityType || '—'}</p>
                    {(() => {
                      const bizAddr = kybDetails.company_information?.business_address ?? kybDetails.onboarding?.business?.BusinessAddress;
                      if (!bizAddr) return null;
                      const str = typeof bizAddr === 'string' ? bizAddr : formatAddress(bizAddr);
                      return str !== '—' ? <p className="text-sm text-[#0F172A]"><strong>Business address:</strong> {str}</p> : null;
                    })()}
                  </div>
                  {/* Address submitted */}
                  {(kybDetails.onboarding?.address && Object.keys(kybDetails.onboarding.address).length > 0) && (
                    <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-1">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Address</p>
                      <p className="text-sm text-[#0F172A]">{formatAddress(kybDetails.onboarding.address)}</p>
                    </div>
                  )}
                  {/* Business details */}
                  <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Business details</p>
                    <p className="text-sm text-[#0F172A]"><strong>Industry:</strong> {kybDetails.onboarding?.industry || '—'}</p>
                    <p className="text-sm text-[#0F172A]"><strong>Monthly revenue:</strong> {kybDetails.onboarding?.monthly_revenue || '—'}</p>
                    <p className="text-sm text-[#0F172A]"><strong>Products / services:</strong> {kybDetails.onboarding?.products_services || '—'}</p>
                    {(kybDetails.onboarding?.websites?.length > 0) && (
                      <p className="text-sm text-[#0F172A]"><strong>Websites:</strong> {kybDetails.onboarding.websites.join(', ')}</p>
                    )}
                    {(kybDetails.onboarding?.business_countries?.length > 0) && (
                      <p className="text-sm text-[#0F172A]"><strong>Business countries:</strong> {kybDetails.onboarding.business_countries.join(', ')}</p>
                    )}
                    <p className="text-sm text-[#0F172A]"><strong>Third party shareholding:</strong> {kybDetails.onboarding?.third_party_shareholding || '—'}</p>
                  </div>
                  {/* Beneficial owners / UBO */}
                  {(kybDetails.onboarding?.beneficial_owners?.length > 0) && (
                    <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-2">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Beneficial owners (UBO)</p>
                      <ul className="list-none space-y-1">
                        {kybDetails.onboarding.beneficial_owners.map((ubo, i) => (
                          <li key={ubo.id || i} className="text-sm text-[#0F172A]">
                            {ubo.name || 'Unnamed'} — {ubo.role || '—'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {/* UBO ID details */}
                  {kybDetails.onboarding?.ubo_id_details && typeof kybDetails.onboarding.ubo_id_details === 'object' && Object.keys(kybDetails.onboarding.ubo_id_details).length > 0 && (
                    <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-3">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">UBO ID details</p>
                      {Object.entries(kybDetails.onboarding.ubo_id_details).map(([id, doc]) => (
                        doc && typeof doc === 'object' && (
                          <div key={id} className="text-sm text-[#0F172A] border-b border-[#DCE4E0] pb-2 last:border-0 last:pb-0">
                            <p><strong>Name:</strong> {[doc.first, doc.middle, doc.last].filter(Boolean).join(' ')}</p>
                            {doc.dob && <p><strong>DOB:</strong> {doc.dob}</p>}
                            {doc.nationality && <p><strong>Nationality:</strong> {doc.nationality}</p>}
                            {doc.id_type && <p><strong>ID type:</strong> {doc.id_type}</p>}
                            {doc.id_country && <p><strong>ID country:</strong> {doc.id_country}</p>}
                            {doc.address && Object.keys(doc.address).length > 0 && <p><strong>Address:</strong> {formatAddress(doc.address)}</p>}
                          </div>
                        )
                      ))}
                    </div>
                  )}
                  {/* Account applicant */}
                  {kybDetails.onboarding?.account_applicant_id && (
                    <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Account applicant</p>
                      <p className="text-sm text-[#0F172A]">{kybDetails.onboarding.account_applicant_id === 'none' ? 'No separate applicant' : kybDetails.onboarding.account_applicant_id}</p>
                    </div>
                  )}
                  {/* Supporting documents */}
                  {(kybDetails.onboarding?.supporting_documents?.length > 0) && (
                    <div className="rounded-xl border border-[#DCE4E0] bg-[#F8FAFC] p-4 space-y-2">
                      <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Supporting documents</p>
                      <ul className="list-none space-y-1 text-sm text-[#0F172A]">
                        {kybDetails.onboarding.supporting_documents.map((doc, i) => (
                          <li key={i}>{doc.type || 'Document'}: {doc.name || '—'} {doc.size ? `(${(doc.size / 1024).toFixed(1)} KB)` : ''}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500 py-4">No KYB details available.</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </BackOfficeLayout>
  );
};

export default BackOfficeMerchants;
